import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import {
  clearJobCandidates,
  clearSelectedJob,
  FETCH_JOB_ACTION,
  FETCH_JOB_CANDIDATES_ACTION,
  fetchJob,
  fetchJobCandidates,
  fetchProfessionalProfile,
} from '~/actions';
import {
  BackButton,
  Box,
  CardSpinner,
  Column,
  FloatingContainer,
  Row,
  Screen,
  Text,
  ZStack,
} from '~/commonComponents';
import { getShouldShowBackgroundCheckBadge } from '~/growthbook';
import { useAppSelector, useIsSuccess, useLoading } from '~/hooks';
import { CheckCircleOutlineIcon, CloseCircleOutlineIcon } from '~/images/icons';
import { BackgroundCheckStatusEnum } from '~/models';
import { IJobReducerState } from '~/reducers/jobReducer/interfaces';
import { Colors } from '~/themes/colors';
import { ProfessionalProfile } from './components';
import { ExperienceOverview } from './containers';
import { TCandidateOverviewPageRoutes } from './Types';

export const CandidateOverview = () => {
  const dispatch = useDispatch();

  const { jobId, professionalId } = useParams<TCandidateOverviewPageRoutes>();

  const { isLoading } = useLoading([FETCH_JOB_ACTION, FETCH_JOB_CANDIDATES_ACTION]);
  const { isSuccess: isSuccessFetchJobs } = useIsSuccess([FETCH_JOB_ACTION]);
  const { isSuccess: isSuccessFetchJobsCandidates } = useIsSuccess([FETCH_JOB_CANDIDATES_ACTION]);

  const { job: currentJob, allCandidates: allCounters } = useAppSelector(
    (state) => state.job as IJobReducerState,
  );
  const profile = useAppSelector((state) => state.user.professionalProfile);
  const currentCounter = allCounters.find((it) => it.candidate.id === professionalId);
  const professional = currentCounter?.candidate || currentJob?.offer_final_reciever || profile;

  const hasBackgroundCheck =
    getShouldShowBackgroundCheckBadge() &&
    currentCounter?.requestor.backgroundCheck.status === BackgroundCheckStatusEnum.Clear;

  useEffect(() => {
    dispatch(clearSelectedJob());
    dispatch(clearJobCandidates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch job data - required to render candidate overview page and determine navigation behavior
  useEffect(() => {
    if (jobId && professionalId) {
      dispatch(fetchJob({ jobId, candidateId: professionalId, requestCandidateOvertime: false }));
    }
  }, [dispatch, jobId, professionalId]);

  // Fetch job candidates (counters) - provides background check status, DSO/office match data, and company match labels
  useEffect(() => {
    if (isSuccessFetchJobs && !!currentJob) {
      dispatch(fetchJobCandidates({ jobId }));
    }
  }, [currentJob, dispatch, isSuccessFetchJobs, jobId]);

  // Fetch professional profile if candidate missing from job counters - ensures experience/skills data is always available
  useEffect(() => {
    if (isSuccessFetchJobsCandidates && !currentCounter?.candidate) {
      dispatch(fetchProfessionalProfile(professionalId));
    }
  }, [currentCounter?.candidate, dispatch, isSuccessFetchJobsCandidates, professionalId]);

  if (isLoading || !currentJob || !professional) {
    return <CardSpinner />;
  }

  return (
    <Screen>
      <FloatingContainer borderRadius={0} flex={1} paddingBlock={54} paddingInline={48}>
        <Row position="relative" alignItems="center" justifyContent="center">
          <ZStack left={0}>
            <BackButton />
          </ZStack>
          <Text bold fontSize={32} textAlign="center">
            Candidate Overview
          </Text>
        </Row>

        <Row gap={20} marginTop={48}>
          <Box flex={1}>
            <ProfessionalProfile
              disableNavigation
              hasBackgroundCheck={hasBackgroundCheck}
              hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
              hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
              job={currentJob}
              professional={professional}
            />
          </Box>
          <Row flex={1} alignItems="center" justifyContent="center" gap={16}>
            <button
              data-cy="candidate-action-reject"
              type="button"
              style={{
                alignItems: 'center',
                background: Colors.warning_500,
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                height: 48,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
            >
              <CloseCircleOutlineIcon color={Colors.white} />
              <Text bold color={Colors.white} fontSize={18}>
                Reject
              </Text>
            </button>
            <button
              data-cy="candidate-action-hire"
              type="button"
              style={{
                alignItems: 'center',
                background: Colors.primary_500,
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                height: 48,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
            >
              <CheckCircleOutlineIcon color={Colors.white} />
              <Text bold color={Colors.white} fontSize={18}>
                Hire
              </Text>
            </button>
          </Row>
        </Row>

        <Row gap={20} marginTop={18}>
          <Column flex={1}>
            <ExperienceOverview
              hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
              hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
              job={currentJob}
              professional={professional}
            />
          </Column>
          <Column flex={1} />
        </Row>
      </FloatingContainer>
    </Screen>
  );
};
